import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContentWithSearch,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  ArrowRight,
  Boxes,
  Clock,
  Factory,
  Loader2,
  Package,
  RefreshCw,
  Settings2,
  Truck,
  User,
} from 'lucide-react';
import { toast } from 'sonner';
import { useBranch } from '../contexts/BranchContext';
import { endgoodApiService, Endgood } from '../services/endgoodApi';
import { bomHeaderApiService, BomHeader } from '../services/bomHeaderApi';
import { bomDetailApiService, BomDetail } from '../services/bomDetailApi';
import { processFlowApiService, ProcessFlow, ProcessStep } from '../services/processFlowApi';
import { wipApiService, WorkInProgressSummary } from '../services/wipApi';
import { workOrderApiService, WorkOrder } from '../services/workOrderApi';
import { materialIssueApiService, MaterialIssue } from '../services/materialIssueApi';
import {
  getBinStatus,
  getBinStatusColors,
  formatCycleTime,
  stationNeedsRefill,
  BIN_SAFETY_MULTIPLIER,
} from '../utils/assemblyLineBinStatus';

interface BomComponentTile {
  id: number;
  name: string;
  requiredPerUnit: number;
  unit: string;
  remainingInBin: number;
  processId: number;
  processName: string;
  rawItemId?: number;
  isInHouse: boolean;
}

interface RoutingStation {
  id: number;
  stationCode: string;
  processName: string;
  operator: string;
  itemsLeft: number;
  componentCount: number;
  cycleTimeSeconds: number;
  processId: number;
  needsRefill: boolean;
}

interface ProductOverview {
  bomCost: number;
  operationCost: number;
  leadTimeDays: number;
  productionTimeDays: number;
  totalParts: number;
  inHouseParts: number;
  externalParts: number;
  productionLotQty: number;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value);

const formatDays = (days: number) => (days > 0 ? `${days} Days` : '-');

const formatQuantity = (qty: number, unit: string) => (qty > 0 ? `${qty} ${unit}` : '-');

const DEFAULT_WORK_ORDER_ID = 29313;
const WORK_ORDERS_PAGE_SIZE = 500;

// TODO: temporary mock — remove once WIP assignedTo / operation assignedOperator is wired
const TEMP_MOCK_WORKERS = [
  'Ramesh Kumar',
  'Suresh Patel',
  'Amit Singh',
  'Priya Sharma',
  'Vikram Joshi',
  'Neha Gupta',
  'Rahul Verma',
  'Anjali Mehta',
];

const getTempMockWorker = (stationId: number) =>
  TEMP_MOCK_WORKERS[Math.abs(stationId) % TEMP_MOCK_WORKERS.length];

const formatWorkOrderLabel = (workOrder: WorkOrder) => {
  const endgoodName =
    workOrder.endgoodName || workOrder.endgood?.endgoodName || 'Unknown endgood';
  return `${workOrder.workOrderNumber} — ${endgoodName}`;
};

const toComponentKey = (type: 'rawItem' | 'endgood', id?: number | null) =>
  id ? `${type}:${id}` : '';

const getWorkOrderEndgoodId = (workOrder: WorkOrder) =>
  workOrder.endgoodId ?? workOrder.endgood?.id;

const getMaterialIssueComponentKey = (issue: MaterialIssue) => {
  const rawItemId = issue.rawItem?.id ?? (issue as MaterialIssue & { rawItemId?: number }).rawItemId;
  if (rawItemId) return toComponentKey('rawItem', rawItemId);

  const endgoodId = issue.endgood?.id ?? (issue as MaterialIssue & { endgoodId?: number }).endgoodId;
  if (endgoodId) return toComponentKey('endgood', endgoodId);

  return '';
};

const AssemblyLineDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();

  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<string>('');
  const [activeWorkOrder, setActiveWorkOrder] = useState<WorkOrder | null>(null);
  const [endgood, setEndgood] = useState<Endgood | null>(null);
  const [activeBom, setActiveBom] = useState<BomHeader | null>(null);
  const [bomDetails, setBomDetails] = useState<BomDetail[]>([]);
  const [processFlow, setProcessFlow] = useState<ProcessFlow | null>(null);
  const [wipTasks, setWipTasks] = useState<WorkInProgressSummary[]>([]);
  const [materialIssuedByComponentKey, setMaterialIssuedByComponentKey] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadWorkOrdersList = useCallback(async (): Promise<WorkOrder[]> => {
    try {
      setLoading(true);
      const response = selectedBranch
        ? await workOrderApiService.getWorkOrdersByBranch(
            String(selectedBranch),
            1,
            WORK_ORDERS_PAGE_SIZE
          )
        : await workOrderApiService.getWorkOrdersWithPagination(1, WORK_ORDERS_PAGE_SIZE);

      if (response.success && response.data) {
        const sorted = [...response.data].sort((a, b) => b.id - a.id);
        setWorkOrders(sorted);
        return sorted;
      }

      setWorkOrders([]);
      return [];
    } catch {
      toast.error('Failed to load work orders');
      setWorkOrders([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [selectedBranch]);

  const loadMaterialIssued = useCallback(async (workOrderId: number) => {
    try {
      const response = await materialIssueApiService.getMaterialIssuesByWorkOrder(
        String(workOrderId),
        1,
        1000
      );
      if (response.success && response.data) {
        const map: Record<string, number> = {};
        response.data.forEach((issue) => {
          const key = getMaterialIssueComponentKey(issue);
          if (!key) return;
          map[key] = (map[key] || 0) + (issue.issueQuantity ?? 0);
        });
        setMaterialIssuedByComponentKey(map);
      } else {
        setMaterialIssuedByComponentKey({});
      }
    } catch {
      setMaterialIssuedByComponentKey({});
    }
  }, []);

  const loadWorkOrderDetails = useCallback(
    async (workOrder: WorkOrder) => {
      const endgoodId = getWorkOrderEndgoodId(workOrder);
      if (!endgoodId) return;
      try {
        setDetailLoading(true);
        const branchId = selectedBranch || workOrder.branchId;

        const bomPromise = workOrder.bomHeader
          ? Promise.resolve(workOrder.bomHeader)
          : workOrder.bomHeaderId
            ? bomHeaderApiService.getBomHeaderById(String(workOrder.bomHeaderId))
            : bomHeaderApiService.getBomHeadersByEndgood(Number(endgoodId)).then((headers) =>
                headers.find((h) => h.isActive) ||
                [...headers].sort((a, b) => (b.id ?? 0) - (a.id ?? 0))[0] ||
                null
              );

        const [endgoodData, bom, wipResponse] = await Promise.all([
          endgoodApiService.getEndgoodById(String(endgoodId)),
          bomPromise,
          wipApiService.filterWIPTasks({
            ...(branchId ? { branchId } : {}),
            endgoodId: Number(endgoodId),
            workOrderNumber: workOrder.workOrderNumber,
          }),
        ]);

        setEndgood(endgoodData);
        setActiveBom(bom);

        let details: BomDetail[] = [];
        if (bom) {
          details = await bomDetailApiService.getBomDetailsByHeader(bom.id);
        }
        setBomDetails(details);

        let routing: ProcessFlow | null = null;
        const flowId = bom?.processFlow?.id ?? bom?.processFlowId;
        if (flowId) {
          const flow = await processFlowApiService.getProcessFlowById(String(flowId));
          const steps = await processFlowApiService.getProcessStepsByProcessFlow(flow.id);
          routing = { ...flow, processSteps: steps };
        }
        setProcessFlow(routing);

        if (wipResponse.success && wipResponse.data) {
          setWipTasks(wipResponse.data);
        } else {
          setWipTasks([]);
        }

        await loadMaterialIssued(workOrder.id);
      } catch {
        toast.error('Failed to load assembly line data');
      } finally {
        setDetailLoading(false);
      }
    },
    [selectedBranch, loadMaterialIssued]
  );

  useEffect(() => {
    loadWorkOrdersList().then((list) => {
      if (list.length === 0) {
        setSelectedWorkOrderId('');
        setActiveWorkOrder(null);
        return;
      }

      setSelectedWorkOrderId((prev) => {
        if (prev && list.some((wo) => String(wo.id) === prev)) {
          return prev;
        }
        const preferred = list.find((wo) => wo.id === DEFAULT_WORK_ORDER_ID);
        return String((preferred ?? list[0]).id);
      });
    });
  }, [loadWorkOrdersList]);

  useEffect(() => {
    if (!selectedWorkOrderId) return;

    let cancelled = false;

    const loadSelectedWorkOrder = async () => {
      try {
        const response = await workOrderApiService.getWorkOrderById(selectedWorkOrderId);
        if (cancelled) return;

        if (response.success && response.data) {
          setActiveWorkOrder(response.data);
          await loadWorkOrderDetails(response.data);
        } else {
          setActiveWorkOrder(null);
        }
      } catch {
        if (!cancelled) {
          toast.error('Failed to load work order details');
        }
      }
    };

    loadSelectedWorkOrder();

    return () => {
      cancelled = true;
    };
  }, [selectedWorkOrderId, loadWorkOrderDetails]);

  const handleWorkOrderChange = (workOrderId: string) => {
    setSelectedWorkOrderId(workOrderId);
  };

  const bomComponents = useMemo<BomComponentTile[]>(() => {
    return bomDetails.map((detail) => {
      const rawItemId = detail.rawItem?.id;
      const componentEndgoodId = detail.endgood?.id ?? detail.endgoodId;
      const isInHouse = Boolean(componentEndgoodId && !rawItemId);
      const componentKey = rawItemId
        ? toComponentKey('rawItem', rawItemId)
        : toComponentKey('endgood', componentEndgoodId);
      const name =
        detail.rawItem?.itemName ||
        detail.endgood?.endgoodName ||
        detail.rawCategory?.categoryName ||
        'Component';
      const requiredPerUnit = detail.quantity ?? detail.totalQuantity ?? 0;
      const remainingInBin = materialIssuedByComponentKey[componentKey] ?? 0;

      return {
        id: detail.id,
        name,
        requiredPerUnit,
        unit: detail.unit || 'pc',
        remainingInBin,
        processId: detail.process?.id ?? 0,
        processName: detail.process?.processName || 'Unassigned',
        rawItemId,
        isInHouse,
      };
    });
  }, [bomDetails, materialIssuedByComponentKey]);

  const overview = useMemo<ProductOverview>(() => {
    const inHouseParts = bomComponents.filter((c) => c.isInHouse).length;
    const externalParts = bomComponents.filter((c) => !c.isInHouse).length;
    const productionLotQty = Number(
      activeWorkOrder?.quantity ??
        (wipTasks.length > 0
          ? Math.max(...wipTasks.map((t) => t.wipQuantity || 0), 0)
          : activeBom?.productionQuantity ?? 0)
    );

    const totalLeadDays = bomDetails.reduce((sum, d) => sum + (d.leadTimeDays || 0), 0);
    const totalCycleSeconds =
      processFlow?.processSteps?.reduce((sum, s) => sum + (s.runTime || s.cycleTime || 0), 0) ?? 0;

    return {
      bomCost: activeBom?.materialCost ?? 0,
      operationCost: (activeBom?.laborCost ?? 0) + (activeBom?.overheadCost ?? 0),
      leadTimeDays: totalLeadDays,
      productionTimeDays: totalCycleSeconds > 0 ? Math.max(1, Math.ceil(totalCycleSeconds / 86400)) : 0,
      totalParts: bomComponents.length,
      inHouseParts,
      externalParts,
      productionLotQty,
    };
  }, [bomComponents, bomDetails, processFlow, wipTasks, activeBom, activeWorkOrder]);

  const routingStations = useMemo<RoutingStation[]>(() => {
    const steps = [...(processFlow?.processSteps ?? [])].sort(
      (a, b) => a.sequenceOrder - b.sequenceOrder
    );

    return steps.map((step: ProcessStep, index: number) => {
      const processId = step.process?.id ?? 0;
      const stationComponents = bomComponents.filter((c) => c.processId === processId);
      const wipAtStation = wipTasks.find((t) => t.processStepId === step.id);
      const assignedOperator =
        wipAtStation?.assignedTo?.trim() || getTempMockWorker(step.id);

      return {
        id: step.id,
        stationCode: `ST-${String(index + 1).padStart(2, '0')}`,
        processName: step.process?.processName || `Process ${index + 1}`,
        operator: assignedOperator,
        itemsLeft: wipAtStation?.wipQuantity ?? overview.productionLotQty,
        componentCount: stationComponents.length,
        cycleTimeSeconds: step.runTime || step.cycleTime || 0,
        processId,
        needsRefill: stationNeedsRefill(stationComponents),
      };
    });
  }, [processFlow, bomComponents, wipTasks, overview.productionLotQty]);

  const handleRefillBin = (component: BomComponentTile) => {
    if (component.rawItemId) {
      toast.info(`Refill requested for ${component.name}`, {
        description: 'Navigate to GRN or store inventory to replenish this bin.',
        action: {
          label: 'Store Inventory',
          onClick: () => navigate('/store-inventory'),
        },
      });
    } else {
      toast.info(`Refill requested for in-house part: ${component.name}`);
    }
  };

  const handleRefresh = async () => {
    const list = await loadWorkOrdersList();
    if (!selectedWorkOrderId) return;

    const stillExists = list.some((wo) => String(wo.id) === selectedWorkOrderId);
    if (!stillExists && list.length > 0) {
      setSelectedWorkOrderId(String(list[0].id));
      return;
    }

    if (!stillExists) return;

    try {
      const response = await workOrderApiService.getWorkOrderById(selectedWorkOrderId);
      if (response.success && response.data) {
        setActiveWorkOrder(response.data);
        await loadWorkOrderDetails(response.data);
      }
    } catch {
      toast.error('Failed to refresh work order details');
    }
  };

  const effectiveBranchId = selectedBranch || activeWorkOrder?.branchId;
  const routedProcessIds = new Set(routingStations.map((station) => station.processId));
  const unassignedComponents = bomComponents.filter(
    (component) => !component.processId || !routedProcessIds.has(component.processId)
  );

  if (loading && workOrders.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        {/* Header */}
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary-200 flex items-center justify-center">
                  <Factory className="h-6 w-6 text-primary-800" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Assembly Line Dashboard</CardTitle>
                  <p className="text-gray-600 mt-1">
                    {activeWorkOrder
                      ? formatWorkOrderLabel(activeWorkOrder)
                      : 'Select a work order to view assembly line status'}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={detailLoading}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${detailLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Product summary */}
        <Card className="border-primary-200 bg-white shadow-lg">
          <CardContent className="space-y-5 pt-6">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                Work Order — Endgood
              </p>
              <Select value={selectedWorkOrderId} onValueChange={handleWorkOrderChange}>
                <SelectTrigger className="w-full max-w-xl border-primary-200">
                  <SelectValue placeholder="Select work order" />
                </SelectTrigger>
                <SelectContentWithSearch>
                  {workOrders.length === 0 ? (
                    <SelectItem value="no-work-orders" disabled>
                      No work orders available
                    </SelectItem>
                  ) : (
                    workOrders.map((workOrder) => (
                      <SelectItem key={workOrder.id} value={String(workOrder.id)}>
                        {formatWorkOrderLabel(workOrder)}
                      </SelectItem>
                    ))
                  )}
                </SelectContentWithSearch>
              </Select>
            </div>

            <div>
              {detailLoading ? (
                <div className="flex-1 flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                </div>
              ) : endgood ? (
                <div className="flex-1 space-y-4">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="h-20 w-20 rounded-lg bg-primary-50 border border-primary-200 flex items-center justify-center overflow-hidden shrink-0">
                      {endgood.drawingImagePath ? (
                        <img
                          src={endgood.drawingImagePath}
                          alt={endgood.endgoodName}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Package className="h-8 w-8 text-primary-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-xl font-bold text-gray-900 truncate">
                        {endgood.partNumber ? `${endgood.partNumber} — ` : ''}
                        {endgood.endgoodName}
                      </h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        SKU: {endgood.sku || 'N/A'}
                        {activeBom ? ` · BOM ${activeBom.bomNumber} v${activeBom.version}` : ''}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <Badge variant="outline" className="border-primary-300 text-primary-700 bg-primary-50">
                          BOM Cost — {formatCurrency(overview.bomCost)}
                        </Badge>
                        <Badge variant="outline" className="border-violet-300 text-violet-700 bg-violet-50">
                          Operation Cost — {formatCurrency(overview.operationCost)}
                        </Badge>
                        <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">
                          Lead Time - {formatDays(overview.leadTimeDays)}
                        </Badge>
                        <Badge variant="outline" className="border-orange-300 text-orange-700 bg-orange-50">
                          Production Time - {formatDays(overview.productionTimeDays)}
                        </Badge>
                        <Badge variant="outline" className="border-slate-300 text-slate-700 bg-slate-50">
                          Lot Qty - {formatQuantity(overview.productionLotQty, 'PCS')}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Card className="border-primary-200 shadow-sm">
                      <CardContent className="pt-4 flex items-center gap-4">
                        <Settings2 className="h-8 w-8 text-primary-600 shrink-0" />
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{overview.totalParts}</p>
                          <p className="text-sm font-medium text-gray-800">No. of Parts</p>
                          <p className="text-xs text-muted-foreground">total components</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-primary-200 shadow-sm">
                      <CardContent className="pt-4 flex items-center gap-4">
                        <Boxes className="h-8 w-8 text-primary-600 shrink-0" />
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{overview.inHouseParts}</p>
                          <p className="text-sm font-medium text-gray-800">Factory OEM</p>
                          <p className="text-xs text-muted-foreground">in-house produced</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-primary-200 shadow-sm">
                      <CardContent className="pt-4 flex items-center gap-4">
                        <Truck className="h-8 w-8 text-primary-600 shrink-0" />
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{overview.externalParts}</p>
                          <p className="text-sm font-medium text-gray-800">Other OEM</p>
                          <p className="text-xs text-muted-foreground">external suppliers</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground py-4">Select a work order to view overview</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Section 2 + 3: Routing status with process-wise BOM */}
        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3">
            <div>
              <CardTitle className="text-lg font-semibold text-gray-800">
                Production Lot —{' '}
                <span className="text-emerald-600">
                  {formatQuantity(overview.productionLotQty, 'PCS')}
                </span>
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Routing stations with BOM components grouped under each process
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                OK (≥{BIN_SAFETY_MULTIPLIER}× required)
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-red-500" />
                Refill needed
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {!effectiveBranchId && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
                Select a branch to load WIP quantities. Bin quantities come from material issued for the active work order.
              </p>
            )}

            {routingStations.length === 0 ? (
              <p className="text-muted-foreground text-sm py-8 text-center">
                No routing defined for this product. Link a process flow to the BOM.
              </p>
            ) : (
              <div className="overflow-x-auto pb-3">
                <div className="flex items-start gap-4 min-w-max">
                  {routingStations.map((station, idx) => {
                    const stationComponents = bomComponents.filter(
                      (component) => component.processId === station.processId
                    );
                    const borderClass = station.needsRefill
                      ? 'border-red-400 ring-1 ring-red-200 bg-red-50/30'
                      : 'border-primary-200 bg-white';

                    return (
                      <React.Fragment key={station.id}>
                        <div className="w-[340px] shrink-0">
                          <div className={`border rounded-xl shadow-sm overflow-hidden ${borderClass}`}>
                            <div className="p-4 border-b border-primary-100 bg-gradient-to-r from-primary-50 to-white">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-muted-foreground font-medium">{station.stationCode}</span>
                                <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-semibold">
                                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                  ACTIVE
                                </span>
                              </div>
                              <p className="font-semibold text-gray-900 text-sm leading-tight mb-2 line-clamp-2">
                                {station.processName}
                              </p>
                              <p className="flex items-center gap-1 text-xs text-muted-foreground mb-3">
                                <User className="h-3 w-3" />
                                {station.operator}
                              </p>
                              <div className="flex items-end justify-between gap-3">
                                <div>
                                  <p className={`text-2xl font-bold ${station.needsRefill ? 'text-red-600' : 'text-emerald-600'}`}>
                                    {station.itemsLeft} left
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {stationComponents.length} components
                                  </p>
                                </div>
                                <p className="flex items-center gap-1 text-xs text-primary-600 mb-1">
                                  <Clock className="h-3 w-3" />
                                  {formatCycleTime(station.cycleTimeSeconds)}
                                </p>
                              </div>
                            </div>

                            <div className="p-3">
                              <div className="flex items-center justify-between mb-3">
                                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                  BOM Components
                                </p>
                                <Badge variant="outline" className="border-primary-200 bg-primary-50 text-primary-700">
                                  {stationComponents.length}
                                </Badge>
                              </div>

                              {stationComponents.length === 0 ? (
                                <p className="text-sm text-muted-foreground border border-dashed border-primary-200 rounded-lg p-4 text-center">
                                  No BOM components mapped to this process.
                                </p>
                              ) : (
                                <div className="space-y-3 max-h-[560px] overflow-y-auto pr-1">
                                  {stationComponents.map((component) => {
                                    const status = getBinStatus(component.remainingInBin, component.requiredPerUnit);
                                    const colors = getBinStatusColors(status);

                                    return (
                                      <div
                                        key={component.id}
                                        className={`relative rounded-xl border p-3 flex flex-col gap-3 shadow-sm ${colors.border} ${colors.cardBg} before:absolute before:left-0 before:top-3 before:bottom-3 before:w-1 before:rounded-full ${colors.leftAccent}`}
                                      >
                                        <div className="flex items-start gap-2 pl-2">
                                          <span className={`h-2.5 w-2.5 rounded-full shrink-0 mt-1 ${colors.dot}`} />
                                          <p className="font-semibold text-gray-900 text-sm leading-snug">{component.name}</p>
                                        </div>
                                        <div className="space-y-1 text-xs text-muted-foreground pl-4">
                                          <p>
                                            Required in Machine —{' '}
                                            <span className="text-gray-700">
                                              {component.requiredPerUnit} {component.unit}
                                            </span>
                                          </p>
                                          <p>
                                            Remaining in Bin —{' '}
                                            <span className={`font-bold ${colors.text}`}>
                                              {component.remainingInBin} {component.unit}
                                            </span>
                                          </p>
                                        </div>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className={`mt-auto w-full text-xs ${
                                            status === 'low'
                                              ? 'border-red-300 text-red-700 hover:bg-red-50'
                                              : 'border-primary-200 text-primary-700 hover:bg-primary-50'
                                          }`}
                                          onClick={() => handleRefillBin(component)}
                                        >
                                          Refill Bin →
                                        </Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {idx < routingStations.length - 1 && (
                          <div className="flex items-center h-28 shrink-0 text-primary-300">
                            <ArrowRight className="h-5 w-5" />
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {unassignedComponents.length > 0 && (
                    <div className="w-[340px] shrink-0">
                      <div className="border border-amber-300 bg-amber-50/40 rounded-xl shadow-sm overflow-hidden">
                        <div className="p-4 border-b border-amber-200 bg-amber-50">
                          <p className="text-xs text-amber-700 font-medium mb-2">UNASSIGNED</p>
                          <p className="font-semibold text-gray-900 text-sm leading-tight">
                            BOM components without matching routing process
                          </p>
                        </div>
                        <div className="p-3 space-y-3 max-h-[620px] overflow-y-auto pr-1">
                          {unassignedComponents.map((component) => {
                            const status = getBinStatus(component.remainingInBin, component.requiredPerUnit);
                            const colors = getBinStatusColors(status);

                            return (
                              <div
                                key={component.id}
                                className={`relative rounded-xl border p-3 flex flex-col gap-3 shadow-sm ${colors.border} ${colors.cardBg} before:absolute before:left-0 before:top-3 before:bottom-3 before:w-1 before:rounded-full ${colors.leftAccent}`}
                              >
                                <div className="flex items-start gap-2 pl-2">
                                  <span className={`h-2.5 w-2.5 rounded-full shrink-0 mt-1 ${colors.dot}`} />
                                  <p className="font-semibold text-gray-900 text-sm leading-snug">{component.name}</p>
                                </div>
                                <div className="space-y-1 text-xs text-muted-foreground pl-4">
                                  <p>
                                    Process — <span className="text-gray-700">{component.processName}</span>
                                  </p>
                                  <p>
                                    Required in Machine —{' '}
                                    <span className="text-gray-700">
                                      {component.requiredPerUnit} {component.unit}
                                    </span>
                                  </p>
                                  <p>
                                    Remaining in Bin —{' '}
                                    <span className={`font-bold ${colors.text}`}>
                                      {component.remainingInBin} {component.unit}
                                    </span>
                                  </p>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className={`mt-auto w-full text-xs ${
                                    status === 'low'
                                      ? 'border-red-300 text-red-700 hover:bg-red-50'
                                      : 'border-primary-200 text-primary-700 hover:bg-primary-50'
                                  }`}
                                  onClick={() => handleRefillBin(component)}
                                >
                                  Refill Bin →
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AssemblyLineDashboard;
